/**
 * Export filtered Bookings table rows to XLSX or PDF.
 */
import * as XLSX from 'xlsx';
import { formatBookingIdDisplay } from './bookingIdDisplay';

export type ExportableBooking = {
  id: number;
  source?: string | null;
  is_merged_pair?: boolean;
  linked_ical_booking_id?: number | null;
  linked_manual_booking_id?: number | null;
  property_nickname?: string | null;
  property_id?: number | null;
  platform?: string | null;
  account_owner?: string | null;
  booking_ref?: string | null;
  guest_name?: string | null;
  check_in_date?: string | null;
  check_out_date?: string | null;
  nights?: number | null;
  adults_count?: number | null;
  children_count?: number | null;
  total_payout_amount?: number | null;
  host_payout_amount?: number | null;
  currency?: string | null;
  status?: string | null;
  updates_count?: number | null;
  last_sync?: string | null;
  created_at?: string | null;
  booked_at_display?: string | null;
  booked_at_fallback?: string | null;
  booked_at?: string | null;
  lead_time?: number | null;
};

export const BOOKINGS_EXPORT_HEADERS = [
  'ID',
  'Last Synced',
  'Property',
  'Platform',
  'Account Owner',
  'Booking Ref',
  'Source',
  'Guest',
  'Check-in',
  'Check-out',
  'Nights',
  'Adults',
  'Children',
  'Amount',
  'Currency',
  'Status',
  'Updates',
  'Booked',
  'Lead Time (days)',
] as const;

function ymd(value?: string | null): string {
  if (!value) return '';
  const s = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s;
}

function amountNumber(b: ExportableBooking): number | null {
  const raw = b.total_payout_amount ?? b.host_payout_amount;
  if (raw == null || !Number.isFinite(Number(raw))) return null;
  return Number(raw);
}

function nightsCell(b: ExportableBooking): string | number {
  if (b.nights == null || Number.isNaN(Number(b.nights))) return '';
  return Number(b.nights);
}

export function bookingsAmountTotalsByCurrency(
  bookings: ExportableBooking[]
): Array<{ currency: string; total: number }> {
  const byCcy = new Map<string, number>();
  for (const b of bookings) {
    const n = amountNumber(b);
    if (n == null) continue;
    const ccy = String(b.currency || 'EUR').trim().toUpperCase() || 'EUR';
    byCcy.set(ccy, (byCcy.get(ccy) || 0) + n);
  }
  return Array.from(byCcy.entries())
    .map(([currency, total]) => ({ currency, total }))
    .sort((a, b) => a.currency.localeCompare(b.currency));
}

export function buildBookingsExportRows(bookings: ExportableBooking[]): (string | number)[][] {
  return bookings.map((b) => {
    const amount = amountNumber(b);
    return [
      formatBookingIdDisplay(b),
      ymd(b.last_sync || b.created_at),
      b.property_nickname || (b.property_id != null ? `Property ${b.property_id}` : ''),
      b.platform || '',
      String(b.account_owner || '').trim() || 'Company',
      b.booking_ref || '',
      b.is_merged_pair || b.source === 'Merged' ? 'Merged' : b.source || '',
      b.guest_name || '',
      ymd(b.check_in_date),
      ymd(b.check_out_date),
      nightsCell(b),
      b.adults_count ?? '',
      b.children_count ?? '',
      amount == null ? '' : Number(amount.toFixed(2)),
      String(b.currency || 'EUR').trim().toUpperCase() || 'EUR',
      b.status || '',
      b.updates_count ?? 0,
      ymd(b.booked_at_display || b.booked_at_fallback || b.booked_at),
      b.lead_time == null ? '' : b.lead_time,
    ];
  });
}

export function buildBookingsExportStamp(d = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function exportBookingsToXlsx(
  bookings: ExportableBooking[],
  filename = `bookings-filtered-${buildBookingsExportStamp()}.xlsx`
): void {
  const rows = buildBookingsExportRows(bookings);
  const aoa: (string | number)[][] = [Array.from(BOOKINGS_EXPORT_HEADERS), ...rows];
  const totals = bookingsAmountTotalsByCurrency(bookings);
  if (totals.length) {
    aoa.push([]);
    for (const t of totals) {
      aoa.push([
        `Total (${t.currency}) · ${bookings.length} stays`,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        Number(t.total.toFixed(2)),
        t.currency,
        '',
        '',
        '',
        '',
      ]);
    }
  }
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(aoa), 'Bookings');
  XLSX.writeFile(workbook, filename);
}

export async function exportBookingsToPdf(
  bookings: ExportableBooking[],
  filename = `bookings-filtered-${buildBookingsExportStamp()}.pdf`
): Promise<void> {
  const { default: jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  doc.setFontSize(14);
  doc.text('Bookings (filtered)', 40, 36);
  doc.setFontSize(9);
  doc.text(`Generated ${new Date().toLocaleString('en-GB')} · ${bookings.length} stay(s)`, 40, 52);

  const body = buildBookingsExportRows(bookings).map((row) => row.map((cell) => String(cell)));
  autoTable(doc, {
    startY: 64,
    head: [Array.from(BOOKINGS_EXPORT_HEADERS)],
    body,
    styles: { fontSize: 6.5, cellPadding: 2, overflow: 'linebreak' },
    headStyles: { fillColor: [66, 66, 66], fontSize: 6.5 },
    margin: { left: 24, right: 24 },
  });

  const totals = bookingsAmountTotalsByCurrency(bookings);
  if (totals.length) {
    const finalY =
      (doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? 64;
    doc.setFontSize(9);
    doc.text(
      `Totals · ${bookings.length} stay(s): ${totals
        .map((t) => `${t.currency} ${t.total.toFixed(2)}`)
        .join(' · ')}`,
      40,
      finalY + 16
    );
  }

  doc.save(filename);
}
